import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Stack } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { AssignmentActivityItemContentFragment } from '@graphql/__generated__/graphql';
import {
  CLAIM_LEAD_MUTATION,
  PASS_LEAD_MUTATION,
} from '@modules/_shared/components/feed/Activity/Assignment';
import { ActionButton } from '@modules/_shared/components/feed-v2/components/ActionButton';
import { useLeadExit } from '@modules/_shared/components/lead/leadExit';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { PassLeadDialog } from '@modules/_shared/components/PassLeadDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { GET_DASHBOARD_NEW_LEADS } from '@modules/dashboard/dashboardQueries';
import { GET_LEAD_AGENT_STATUS } from '@modules/lead-details/components/cards/LeadDetailsStatusCard';
import { GET_LEAD_DETAILS } from '@modules/lead-details/LeadDetailsDrawer';
import { GET_LEAD_AGENTS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

export const ASSIGNMENT_ACTIVITY_ITEM_CONTENT_FRAGMENT = gql(/* GraphQL */ `
  fragment AssignmentActivityItemContent on activities {
    id
    lead_id
    assigned_to
  }
`);

type AssignmentActivityItemContentProps = {
  activity: AssignmentActivityItemContentFragment;
  isIndirect: boolean;
  done: boolean;
};

export const AssignmentActivityItemContent = ({
  activity,
  isIndirect,
  done,
}: AssignmentActivityItemContentProps) => {
  const { t } = useLocale();
  // Out of the lead once it is passed: up a route from /leads/:id, closed
  // in place when the drawer sits over another page (the prospection map).
  const exitLead = useLeadExit();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const [passDialogOpen, setPassDialogOpen] = useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  // Mutations for assignment actions
  const [claimLead, { loading: claimLoading }] = useMutation(
    CLAIM_LEAD_MUTATION,
    {
      refetchQueries: [
        GET_LEAD_DETAILS,
        GET_LEAD_AGENT_STATUS,
        GET_LEAD_AGENTS,
        GET_DASHBOARD_NEW_LEADS,
      ],
      onCompleted: () => {
        emitFeedRefresh();
      },
      onError: setErrorLike,
    },
  );

  const [passLead, { loading: passLoading }] = useMutation(PASS_LEAD_MUTATION, {
    refetchQueries: [
      GET_LEAD_DETAILS,
      GET_LEAD_AGENT_STATUS,
      GET_LEAD_AGENTS,
      GET_DASHBOARD_NEW_LEADS,
    ],
    update: cache => {
      if (!isAdmin && activity.lead_id) {
        cache.evict({ id: `leads:${activity.lead_id}` });
      }
    },
    onCompleted: () => {
      emitFeedRefresh();
      if (!isAdmin) {
        exitLead();
      }
    },
    onError: setErrorLike,
  });

  const actionDisabled = useMemo(() => {
    // Check if assignment action data is available
    const canPerformAssignmentAction =
      activity.lead_id != null && activity.assigned_to != null;

    return claimLoading || passLoading || !canPerformAssignmentAction;
  }, [activity.lead_id, activity.assigned_to, claimLoading, passLoading]);

  // Assignment action handlers
  const handleClaimLead = useCallback(() => {
    if (!activity.lead_id || !activity.assigned_to) {
      return;
    }
    claimLead({
      variables: {
        lead_id: activity.lead_id,
        user_id: activity.assigned_to,
      },
    });
  }, [activity.lead_id, activity.assigned_to, claimLead]);

  const handlePassLead = useCallback(
    (note: string) => {
      if (!activity.lead_id || !activity.assigned_to) {
        return;
      }
      setPassDialogOpen(false);
      passLead({
        variables: {
          lead_id: activity.lead_id,
          user_id: activity.assigned_to,
          note,
        },
      });
    },
    [activity.lead_id, activity.assigned_to, passLead],
  );

  if (isIndirect || done) {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <ActionButton
          variant="negative"
          disabled={actionDisabled}
          onClick={() => {
            setPassDialogOpen(true);
          }}
        >
          {t('Pass')}
        </ActionButton>
        <ActionButton
          variant="positive"
          disabled={actionDisabled}
          onClick={handleClaimLead}
        >
          {t('Claim')}
        </ActionButton>
      </Stack>
      <PassLeadDialog
        open={passDialogOpen}
        onClose={() => setPassDialogOpen(false)}
        onReasonSelect={handlePassLead}
        loading={passLoading}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
